import { subMonths, startOfDay, endOfDay } from "date-fns";
import { GUIDELINE_ABBREVIATIONS } from "../../utils/guidelineAbbreviations";

export const RANGE_KEYS = {
  ALL: "all",
  LAST_3M: "3m",
  LAST_6M: "6m",
  LAST_12M: "12m",
  CUSTOM: "custom",
};

export const RANGE_OPTIONS = [
  { key: RANGE_KEYS.ALL, label: "All Time" },
  { key: RANGE_KEYS.LAST_3M, label: "Last 3 Months" },
  { key: RANGE_KEYS.LAST_6M, label: "Last 6 Months" },
  { key: RANGE_KEYS.LAST_12M, label: "Last 12 Months" },
  { key: RANGE_KEYS.CUSTOM, label: "Custom Range" },
];

const ABBREV_TO_NAME = Object.entries(GUIDELINE_ABBREVIATIONS).reduce(
  (acc, [name, code]) => {
    acc[code] = name.replace(/_/g, " ");
    return acc;
  },
  {},
);

const KNOWN_CODES = new Set(Object.values(GUIDELINE_ABBREVIATIONS));

/**
 * Pull a 3-letter guideline code from a topicref href.
 *   1. A 3-letter token at the start of the filename (e.g. "ABG_..." or "KUG-...").
 *   2. A 3-letter token at the start of the parent folder (e.g. "KUG-general/...").
 * Returns null when nothing matches; caller decides how to bucket those.
 */
export const extractGuidelineCode = (href) => {
  if (!href) return null;
  const parts = href.split("/");
  const filename = parts[parts.length - 1] || "";
  const folder = parts.length > 1 ? parts[parts.length - 2] : "";

  const filenameMatch = filename.match(/^([A-Z]{3})[_-]/);
  if (filenameMatch && KNOWN_CODES.has(filenameMatch[1])) {
    return filenameMatch[1];
  }

  const folderMatch = folder.match(/^([A-Z]{3})[_-]/);
  if (folderMatch && KNOWN_CODES.has(folderMatch[1])) {
    return folderMatch[1];
  }

  return null;
};

export const getGuidelineDisplayName = (code) =>
  code ? (ABBREV_TO_NAME[code] ?? code) : "Other";

/**
 * Read the first <category> element's text from a parsed topic dita doc.
 * Stakeholder contract (May 2026): each topic has at most one <category>.
 * Returns null when absent so the card omits the chip and the topic falls
 * outside any guideline filter.
 */
export const getTopicCategory = (topicDoc) => {
  const raw = topicDoc?.getElementsByTagName("category")?.[0]?.textContent;
  const trimmed = raw ? String(raw).trim() : "";
  return trimmed || null;
};

/**
 * Build the guideline option list for the dropdown from codes that actually
 * appear in the current ditamap, sorted alphabetically by display name.
 */
export const buildGuidelineOptions = (codes) => {
  const unique = Array.from(new Set(codes.filter(Boolean)));
  return unique
    .map((code) => ({ code, label: getGuidelineDisplayName(code) }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Return inclusive [from, to] Date bounds for a preset range.
 * `now` is injectable for testability. Returns null for "all" (unbounded).
 */
export const getRangeBounds = (rangeKey, now = new Date()) => {
  switch (rangeKey) {
    case RANGE_KEYS.LAST_3M:
      return { from: startOfDay(subMonths(now, 3)), to: endOfDay(now) };
    case RANGE_KEYS.LAST_6M:
      return { from: startOfDay(subMonths(now, 6)), to: endOfDay(now) };
    case RANGE_KEYS.LAST_12M:
      return { from: startOfDay(subMonths(now, 12)), to: endOfDay(now) };
    case RANGE_KEYS.ALL:
    default:
      return null;
  }
};

const parseISODate = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const readCardDate = (topicDoc) => {
  const raw = topicDoc
    ?.getElementsByTagName("created")?.[0]
    ?.attributes?.date?.value;
  return parseCreatedDate(raw);
};

// `<created date>` values in update folder vary format like: "2026-04-01", "2026-04", "2025-12-1",
// "01-12-2025", and reject commented out strings like "[DATE_FORMATTED_BY_yyyy-MM-dd]"/
export const parseCreatedDate = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s || s.startsWith("[")) return null;
  let m = s.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?$/);
  if (m) {
    const [, y, mo, d] = m;
    return new Date(Number(y), Number(mo) - 1, d ? Number(d) : 1);
  }
  m = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (m) {
    const [, d, mo, y] = m;
    return new Date(Number(y), Number(mo) - 1, Number(d));
  }
  const fallback = new Date(s);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const toISODate = (d) =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "";


export const parseFiltersFromSearchParams = (searchParams) => {
  const rawRange = searchParams.get("range");
  const range = Object.values(RANGE_KEYS).includes(rawRange)
    ? rawRange
    : RANGE_KEYS.ALL;
  const from = range === RANGE_KEYS.CUSTOM ? parseISODate(searchParams.get("from")) : null;
  const to = range === RANGE_KEYS.CUSTOM ? parseISODate(searchParams.get("to")) : null;
  const guidelinesRaw = searchParams.get("guideline") || "";
  const guidelines = guidelinesRaw
    ? guidelinesRaw.split(",").map((s) => s.trim()).filter(Boolean)
    : [];
  return { range, from, to, guidelines };
};

export const applyFiltersToSearchParams = (searchParams, filters) => {
  const next = new URLSearchParams(searchParams);
  if (filters.range && filters.range !== RANGE_KEYS.ALL) {
    next.set("range", filters.range);
  } else {
    next.delete("range");
  }
  if (filters.range === RANGE_KEYS.CUSTOM && filters.from && filters.to) {
    next.set("from", toISODate(filters.from));
    next.set("to", toISODate(filters.to));
  } else {
    next.delete("from");
    next.delete("to");
  }
  if (filters.guidelines && filters.guidelines.length > 0) {
    next.set("guideline", filters.guidelines.join(","));
  } else {
    next.delete("guideline");
  }
  return next;
};

/**
 * Resolve the active date bounds — preset or custom — that the predicate uses.
 * Returns null when no date constraint should apply.
 */
export const resolveDateBounds = (filters, now = new Date()) => {
  if (filters.range === RANGE_KEYS.CUSTOM) {
    if (!filters.from || !filters.to) return null;
    return { from: startOfDay(filters.from), to: endOfDay(filters.to) };
  }
  return getRangeBounds(filters.range, now);
};

export const cardMatchesFilters = (card, filters, now = new Date()) => {
  const bounds = resolveDateBounds(filters, now);
  if (bounds) {
    if (!card.date) return false;
    if (card.date < bounds.from || card.date > bounds.to) return false;
  }
  if (filters.guidelines && filters.guidelines.length > 0) {
    if (!card.guidelineCode) return false;
    if (!filters.guidelines.includes(card.guidelineCode)) return false;
  }
  return true;
};
